const POLL_INTERVAL_MS = 100;
const MAX_WAIT_MS = 20_000;
const POST_READY_SETTLE_MS = 250;
const SCROLL_MARGIN_TOP_PX = 24;

/** Set on the embedded month-view week card when the table body has rendered (not the skeleton). */
export const PENDING_MONTH_WEEK_READY_ATTR = 'data-pending-month-week-ready';

function findScrollableAncestor(el: HTMLElement): HTMLElement {
  let parent: HTMLElement | null = el.parentElement;
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight;
    if (canScrollY) return parent;
    parent = parent.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

function scrollAnchor(el: HTMLElement, smooth: boolean): void {
  el.scrollIntoView({
    behavior: smooth ? 'smooth' : 'auto',
    block: 'start',
    inline: 'nearest',
  });

  const scrollContainer = findScrollableAncestor(el);
  const isDocScroll =
    scrollContainer === document.documentElement || scrollContainer === document.body;
  const elRect = el.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const currentScrollTop = isDocScroll
    ? window.scrollY || document.documentElement.scrollTop
    : scrollContainer.scrollTop;
  const targetScrollTop = Math.max(
    0,
    currentScrollTop + (elRect.top - containerRect.top) - SCROLL_MARGIN_TOP_PX
  );

  if (isDocScroll) {
    window.scrollTo({ top: targetScrollTop, behavior: smooth ? 'smooth' : 'auto' });
  } else {
    scrollContainer.scrollTo({ top: targetScrollTop, behavior: smooth ? 'smooth' : 'auto' });
  }
}

/**
 * Count rendered + ready week cards in the month stacked view.
 * A week is "ready" once its embedded layout (not the skeleton) has rendered.
 */
function getWeekStats(): { rendered: number; ready: number } {
  const cards = document.querySelectorAll(`[${PENDING_MONTH_WEEK_READY_ATTR}="true"]`);
  return { rendered: cards.length, ready: cards.length };
}

/**
 * Scroll to a pending month week anchor (`#pending-month-ts-…`).
 *
 * Strategy: do NOT scroll while content is loading (that causes the "wrong position"
 * jump). Instead wait until either (a) every week card has rendered its real table,
 * or (b) we hit a max budget — then perform a single scroll.
 */
const noop = (): void => {
  // Returned when there is nothing to do; lets the caller always invoke the cleanup.
};

export function scrollToPendingMonthTimesheetAnchor(
  anchorId: string,
  options: { expectedWeekCount?: number } = {}
): () => void {
  if (!anchorId) return noop;

  const expectedWeekCount = options.expectedWeekCount ?? 0;

  let cancelled = false;
  let pollTimer: number | null = null;
  let settleTimer: number | null = null;
  const startedAt = Date.now();

  const stop = () => {
    cancelled = true;
    if (pollTimer !== null) window.clearTimeout(pollTimer);
    if (settleTimer !== null) window.clearTimeout(settleTimer);
    pollTimer = settleTimer = null;
  };

  const finalScroll = () => {
    if (cancelled) return;
    const el = document.getElementById(anchorId);
    if (el) scrollAnchor(el, true);
    stop();
  };

  const scheduleSettleAndScroll = () => {
    if (settleTimer !== null) return;
    settleTimer = window.setTimeout(finalScroll, POST_READY_SETTLE_MS);
  };

  const tick = () => {
    if (cancelled) return;

    const anchorEl = document.getElementById(anchorId);
    const { ready } = getWeekStats();
    const allReady = expectedWeekCount > 0 && ready >= expectedWeekCount;
    const budgetExhausted = Date.now() - startedAt >= MAX_WAIT_MS;

    if (anchorEl && (allReady || budgetExhausted)) {
      scheduleSettleAndScroll();
      return;
    }

    pollTimer = window.setTimeout(tick, POLL_INTERVAL_MS);
  };

  tick();
  return stop;
}
